/**
 * Write, retoggle, or delete one paragraph note.
 *
 * This is the create flow as well as the editor: no row exists until Save, so the agent-visibility
 * choice always precedes the first time a note could reach a model, and an abandoned "+" click
 * cannot leave a note whose splice would be "[Author's note: ]".
 */
import React, { useState } from 'react'
import { Dialog, DialogHeader } from '@astryxdesign/core/Dialog'
import PillButton from './PillButton'
import { IconButton } from '@astryxdesign/core/IconButton'
import { CheckboxInput } from '@astryxdesign/core/CheckboxInput'
import { Switch } from '@astryxdesign/core/Switch'
import { TextArea } from '@astryxdesign/core/TextArea'
import { HStack, VStack } from '@astryxdesign/core/Layout'
import { Text } from '@astryxdesign/core/Text'
import { Info } from 'lucide-react'

/** Matches NOTE_BODY_MAX_LENGTH on the server, which trims and truncates to the same figure. */
const NOTE_BODY_MAX_LENGTH = 4000

export interface ParagraphNoteModalProps {
  /** null when creating; the existing note when editing. */
  note: ParagraphNote | null
  onSave: (body: string, isAgentContext: boolean, isFootnote: boolean) => void
  onDelete: () => void
  onClose: () => void
}

export default function ParagraphNoteModal({
  note,
  onSave,
  onDelete,
  onClose
}: ParagraphNoteModalProps): React.JSX.Element {
  const [body, setBody] = useState(note?.body ?? '')
  // Default on: most notes are steering instructions for the agent, and the toggle is visible
  // here before the first save, so the choice is never made on the writer's behalf.
  const [isAgentContext, setIsAgentContext] = useState(note?.isAgentContext ?? true)
  const [isFootnote, setIsFootnote] = useState(note?.isFootnote ?? false)
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
  // Mirrors the server's sanitizeNoteBody, which strips [ and ] so a body can never forge a
  // marker. Checking only trim() here would let a bracket-only body (e.g. "[]") save as an empty
  // note that silently never reaches the agent: listAgentNotes filters body <> '', so the writer
  // would believe they wrote an agent-visible note that in fact never arrives.
  const isBodyEmpty = body.replace(/[[\]]/g, '').trim().length === 0

  return (
    <Dialog
      isOpen
      onOpenChange={(open: boolean) => !open && onClose()}
      variant="standard"
      // 'form', not the default 'info': a backdrop click on an 'info' dialog would silently
      // discard whatever the writer had typed.
      purpose="form"
      width={640}
      // 90vh, not 70: maxHeight is a ceiling, not a height, so on a roomy screen the dialog is
      // still only as tall as its content. It matters on a short one — at a 720px viewport the
      // 10-row textarea plus the rest of the dialog needs ~520px, and a 70vh cap of 504px would
      // have scrolled the body and shown fewer than the ten lines this is sized for.
      maxHeight="90vh"
    >
      <DialogHeader
        title={note ? 'Note' : 'Add a note'}
        subtitle={note?.isOrphaned ? 'The paragraph this was attached to has changed.' : undefined}
        onOpenChange={() => onClose()}
      />
      <div className="paragraph-note-modal-scroll">
        <VStack gap={3}>
          <TextArea
            label="Note"
            isLabelHidden
            value={body}
            onChange={setBody}
            placeholder="What should change about this paragraph?"
            rows={10}
            maxLength={NOTE_BODY_MAX_LENGTH}
            hasAutoFocus
          />
          <CheckboxInput
            label="Publish as a footnote"
            description="Readers see this note in the published story, numbered, with the text at the foot of the chapter."
            value={isFootnote}
            onChange={setIsFootnote}
          />
          <HStack gap={1} vAlign="center">
            <Switch
              label="Visible to the writing agent"
              // A published footnote cannot also be hidden from the agent — the reader sees it, so
              // "private" is not a coherent state. Forced on and locked rather than silently
              // overridden on save, so the writer can see why it cannot be changed. The server
              // enforces the same rule in parseMutations, since the client is not the authority.
              value={isFootnote || isAgentContext}
              isDisabled={isFootnote}
              disabledMessage="A footnote is published to readers, so the agent can always read it."
              onChange={setIsAgentContext}
            />
            {/* The explanation moved out of the Switch's `description` and behind this icon: the
                two-line field pushed the footer down and the sentence is only wanted once, when
                the writer is deciding what the toggle does. */}
            <IconButton
              variant="ghost"
              size="sm"
              icon={<Info size={14} />}
              label="What the writing agent sees"
              tooltip="On, the agent reads this note alongside the paragraph it belongs to. Off, the note is yours alone and never reaches a model."
            />
          </HStack>
        </VStack>
      </div>
      <div className="paragraph-note-modal-footer">
        {isConfirmingDelete ? (
          <VStack gap={2}>
            <Text type="supporting">Delete this note? This cannot be undone.</Text>
            <HStack gap={2} justify="end">
              <PillButton
                variant="secondary"
                label="Cancel"
                onClick={() => setIsConfirmingDelete(false)}
              />
              <PillButton variant="destructive" label="Delete" onClick={onDelete} />
            </HStack>
          </VStack>
        ) : (
          <HStack gap={2} justify="between">
            <div>
              {note && (
                <PillButton
                  variant="ghost"
                  label="Delete"
                  onClick={() => setIsConfirmingDelete(true)}
                />
              )}
            </div>
            <HStack gap={2} justify="end">
              <PillButton variant="secondary" label="Cancel" onClick={onClose} />
              <PillButton
                variant="primary"
                label={note ? 'Save' : 'Add note'}
                isDisabled={isBodyEmpty}
                onClick={() => onSave(body, isFootnote || isAgentContext, isFootnote)}
              />
            </HStack>
          </HStack>
        )}
      </div>
    </Dialog>
  )
}
